import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import React, {useCallback, useState} from 'react';
import styles from '../../styles';
import {useTranslation} from 'react-i18next';
import {pauseShopifySubscription} from '../../api/membershipAPI';
import {DateTime} from 'luxon';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  quantity?: number;
  pauseStartDate: DateTime;
  shopifySubscriptionId?: number;
  onStart?: () => void;
  onSuccess?: () => Promise<void>;
  onError?: () => void;
}

const PauseMembershipModal: React.FC<Props> = ({
  open,
  setOpen,
  shopifySubscriptionId,
  quantity,
  pauseStartDate,
  onStart,
  onSuccess,
  onError,
}) => {
  const {t} = useTranslation();

  const [pauseLength, setPauseLength] = useState<number>(1);
  const [pending, setPending] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handlePauseShopifySubscription = useCallback(async () => {
    if (!shopifySubscriptionId) {
      alert(t('membership.unknownSubscription'));
      return;
    }

    setPending(true);
    if (onStart) {
      onStart();
    }
    const pauseEndDate = pauseStartDate.plus({months: pauseLength}).toJSDate();
    const res = await pauseShopifySubscription({
      shopifySubscriptionId,
      pauseEndDate,
    });
    setPending(false);
    if (res) {
      setOpen(false);
      if (onSuccess) {
        await onSuccess();
      }
    } else {
      if (onError) {
        onError();
      }
    }
  }, [
    shopifySubscriptionId,
    onStart,
    pauseStartDate,
    pauseLength,
    t,
    setOpen,
    onSuccess,
    onError,
  ]);

  const handlePauseLengthChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setPauseLength(event.target.value as number);
    },
    [],
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={styles.modal}>
        <Typography variant="h4" sx={{mb: 2}}>
          {t('membership.pauseMembership')}
        </Typography>
        <ul>
          {(
            t('membership.pauseMembershipInstructions', {
              returnObjects: true,
            }) as string[]
          ).map(instruction => (
            <li key={instruction}>
              <Typography variant="body1">{instruction}</Typography>
            </li>
          ))}
        </ul>
        <FormControl fullWidth sx={{mb: 2}}>
          <InputLabel id="pause-membership-select-label">
            {t('membership.pauseMembershipDuration')}
          </InputLabel>
          <Select
            labelId="pause-membership-select-label"
            id="pause-membership-select"
            value={pauseLength}
            label={t('membership.pauseMembershipDuration')}
            onChange={handlePauseLengthChange}>
            {Array.from({length: 5}, (_, i) => i + 1).map(month => (
              <MenuItem key={month} value={month}>
                {t('membership.pauseDuration', {pauseLength: month})}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {quantity && quantity > 1 && (
          <Alert severity="warning" variant="outlined" sx={{mb: 2}}>
            {t('membership.thisWillAffectAllSubscriptions', {
              quantity,
            })}
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}>
          {shopifySubscriptionId && (
            <Button
              variant="outlined"
              onClick={handlePauseShopifySubscription}
              disabled={pending}
              sx={{mr: 1}}>
              {t('membership.pauseMembership')}
            </Button>
          )}
          <Button variant="contained" onClick={handleClose}>
            {t('general.close')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PauseMembershipModal;
